import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import pusher from "@/services/pusherService";
import useAuth from "@/hooks/stores/useAuth";
import useGlobalStore from "@/hooks/stores/useGlobalStore";
import candidateService from "@/services/candidateService";
import useToastStore from "@/components/Common/Toast/toastStore";

const CR_IDS_STORAGE_KEY = "kernel_contact_request_channel_ids";

function readStoredContactRequestIds() {
  try {
    const raw = localStorage.getItem(CR_IDS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return [...new Set(parsed.map((x) => Number(x)).filter((n) => !Number.isNaN(n)))];
  } catch {
    return [];
  }
}

export function rememberContactRequestChannelId(id) {
  if (id == null || Number.isNaN(Number(id))) return;
  const n = Number(id);
  const merged = [...new Set([...readStoredContactRequestIds(), n])];
  try {
    localStorage.setItem(CR_IDS_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* ignore */
  }
}

function normalizeListPayload(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function idsFromContactRequestList(list) {
  const ids = new Set();
  for (const req of list) {
    const id = req?.id ?? req?.contact_request_id;
    if (id != null && !Number.isNaN(Number(id))) ids.add(Number(id));
  }
  return [...ids];
}

function contactRequestIdsFromApplicationsPayload(raw) {
  const rows = Array.isArray(raw) ? raw : raw?.data;
  if (!Array.isArray(rows)) return [];
  const ids = new Set();
  for (const row of rows) {
    const id =
      row?.contact_request_id ??
      row?.scheduling_contact_request_id ??
      row?.contact_request?.id ??
      row?.latest_contact_request?.id;
    if (id != null && !Number.isNaN(Number(id))) ids.add(Number(id));
  }
  return [...ids];
}

async function resolveContactRequestIdsToSubscribe() {
  const fromStorage = readStoredContactRequestIds();
  const idSet = new Set(fromStorage);

  try {
    const data = await candidateService.listCandidateContactRequests();
    for (const id of idsFromContactRequestList(normalizeListPayload(data))) {
      idSet.add(id);
    }
  } catch {
    /* list route may not exist yet */
  }

  if (idSet.size === 0) {
    try {
      const raw = await candidateService.getCandidateApplications();
      for (const id of contactRequestIdsFromApplicationsPayload(raw)) {
        idSet.add(id);
      }
    } catch {
      /* ignore */
    }
  }

  return [...idSet];
}

function shouldNotifyCandidateOfMessage(payload, userID) {
  const apiMsg = payload?.message ?? payload?.data ?? payload;
  if (!apiMsg || typeof apiMsg !== "object") return false;
  const uid = Number(userID);
  const sid = apiMsg.sender_id != null ? Number(apiMsg.sender_id) : null;
  const rid = apiMsg.receiver_id != null ? Number(apiMsg.receiver_id) : null;
  if (sid != null && !Number.isNaN(sid) && sid === uid) return false;
  if (sid != null && !Number.isNaN(sid) && sid !== uid) return true;
  if (rid != null && !Number.isNaN(rid) && rid === uid) return true;
  return false;
}

/**
 * Subscribe to private-contact-request.{id} for each known contact request
 * so the candidate gets a bell + toast when the recruiter replies.
 */
export function useCandidateContactRequestChannels() {
  const { userID, userRole } = useAuth();
  const { setHasUnreadNotifications } = useGlobalStore();
  const location = useLocation();

  useEffect(() => {
    if (userRole?.toLowerCase() !== "candidate" || userID == null) {
      return undefined;
    }

    const run = { cancelled: false, subs: [] };

    (async () => {
      const reqIds = await resolveContactRequestIdsToSubscribe();
      if (run.cancelled || reqIds.length === 0) {
        if (import.meta.env.DEV && reqIds.length === 0) {
          console.warn(
            "[contact-request notify] No contact request ids found. Ensure API returns ids (scheduling/candidate/contact-request or applications), or submit a contact request once to store id.",
          );
        }
        return;
      }

      for (const reqId of reqIds) {
        if (run.cancelled) return;
        const channelName = `private-contact-request.${reqId}`;
        const channel = pusher.subscribe(channelName);
        const handler = (payload) => {
          if (!shouldNotifyCandidateOfMessage(payload, userID)) return;
          const s = useGlobalStore.getState();
          if (
            s.contactRequestChatDialogOpen &&
            s.activeContactRequestChatId != null &&
            Number(s.activeContactRequestChatId) === Number(reqId)
          ) {
            return;
          }
          setHasUnreadNotifications(true);
          useToastStore.showToast({
            message:
              "You have a new message — the recruiter replied to your contact request.",
            type: "info",
          });
        };
        channel.bind("ContactRequestMessageSent", handler);
        channel.bind(".ContactRequestMessageSent", handler);
        run.subs.push({ channel, handler, channelName });
      }
    })();

    return () => {
      run.cancelled = true;
      run.subs.forEach(({ channel, handler, channelName }) => {
        try {
          channel.unbind("ContactRequestMessageSent", handler);
          channel.unbind(".ContactRequestMessageSent", handler);
          pusher.unsubscribe(channelName);
        } catch {
          /* ignore */
        }
      });
      run.subs.length = 0;
    };
  }, [userRole, userID, setHasUnreadNotifications, location.pathname]);
}
